import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiCalendar, FiClock, FiPackage } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { ordersAPI, OrderResponse } from '../api/orders';
import { formatCurrency } from '../utils/currency';
import { getStatusLabel } from '../utils/orderStatus';

const Container = styled.div`
  min-height: calc(100vh - var(--header-height));
  padding: 2rem 1rem;
  max-width: 1200px;
  margin: 0 auto;
  margin-top: var(--header-height);

  @media (min-width: 768px) {
    padding: 3rem 2rem;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 2rem;
  color: var(--text-color);
`;

const EmptyState = styled.div`
  background: var(--white);
  border-radius: 8px;
  padding: 4rem 2rem;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const EmptyIcon = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;

  svg {
    width: 64px;
    height: 64px;
    color: var(--gray-dark);
  }
`;

const EmptyTitle = styled.h2`
  font-size: 1.5rem;
  color: var(--text-color);
  margin-bottom: 1rem;
`;

const EmptyText = styled.p`
  color: var(--gray-dark);
  margin-bottom: 2rem;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background-color: var(--primary-color);
  color: var(--white);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.2s;
  text-decoration: none;

  &:hover {
    background-color: var(--primary-dark);
  }
`;

const OrdersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const OrderCard = styled.div`
  background: var(--white);
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const OrderNumber = styled.h3`
  font-size: 1.125rem;
  color: var(--text-color);
`;

const OrderStatus = styled.span<{ $status: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  background-color: ${props => {
    switch (props.$status) {
      case 'pending':
        return 'var(--warning-light)';
      case 'confirmed':
        return 'var(--info-light)';
      case 'ready':
        return 'var(--success-light)';
      case 'completed':
        return 'var(--success-color)';
      default:
        return 'var(--gray-light)';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'pending':
        return 'var(--warning-dark)';
      case 'confirmed':
        return 'var(--info-dark)';
      case 'ready':
        return 'var(--success-dark)';
      case 'completed':
        return 'var(--white)';
      default:
        return 'var(--text-color)';
    }
  }};
`;

const OrderInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  color: var(--gray-dark);
  font-size: 0.875rem;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const OrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [orders, setOrders] = useState<OrderResponse[]>([]);
  const [loading, setLoading] = useState(true);

  // Immediately redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const fetchOrders = async () => {
      try {
        const userOrders = await ordersAPI.getUserOrders();
        setOrders(userOrders);
      } catch (error) {
        console.error('Failed to fetch orders:', error);
        // If we get a 401, redirect to login
        if ((error as any)?.response?.status === 401) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [isAuthenticated]);

  const handleShopNow = () => {
    navigate('/products');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatTime = (timeString: string) => {
    return timeString.substring(0, 5); // Get HH:MM from HH:MM:SS
  };

  // Don't render anything if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <Container>
        <Title>{t('accountOrders.title')}</Title>
        <EmptyState>
          <EmptyText>{t('accountOrders.loading')}</EmptyText>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Title>{t('accountOrders.title')}</Title>

      {orders.length === 0 ? (
        <EmptyState>
          <EmptyIcon>
            <FiPackage />
          </EmptyIcon>
          <EmptyTitle>{t('orders.emptyTitle')}</EmptyTitle>
          <EmptyText>
            {t('orders.emptyDescription')}
          </EmptyText>
          <Button onClick={handleShopNow}>
            {t('orders.startShoppingButton')}
          </Button>
        </EmptyState>
      ) : (
        <OrdersList>
          {orders.map((order) => (
            <OrderCard key={order.order_id} onClick={() => navigate(`/orders/${order.order_id}`)}>
              <OrderHeader>
                <OrderNumber>{t('accountOrders.orderNumberLabel', { number: order.order_number })}</OrderNumber>
                <OrderStatus $status={order.status.toLowerCase()}>
                  {getStatusLabel(order.status, t)}
                </OrderStatus>
              </OrderHeader>
              <OrderInfo>
                <InfoItem>
                  <FiCalendar />
                  <span>{formatDate(order.created_at)}</span>
                </InfoItem>
                <InfoItem>
                  <FiClock />
                  <span>{t('orders.pickupLabel', { date: formatDate(order.pickup_date), time: formatTime(order.pickup_time) })}</span>
                </InfoItem>
                <InfoItem>
                  <span>{formatCurrency(order.total_amount)}</span>
                </InfoItem>
              </OrderInfo>
            </OrderCard>
          ))}
        </OrdersList>
      )}
    </Container>
  );
};

export default OrdersPage;